import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

/**
 * Validate if a script element should be injected
 */
const isValidScript = (element: HTMLScriptElement): boolean => {
  return !!(element.src || element.innerHTML.trim());
};

/**
 * Validate if a meta element should be injected
 */
const isValidMeta = (element: HTMLMetaElement): boolean => {
  const hasIdentifier =
    element.name ||
    element.getAttribute('property') ||
    element.getAttribute('charset');
  const hasContent = element.content || element.getAttribute('charset');
  return !!(hasIdentifier && hasContent);
};

/**
 * Validate if a link element should be injected
 */
const isValidLink = (element: HTMLLinkElement): boolean => {
  return !!element.href;
};

/**
 * Validate if a style element should be injected
 */
const isValidStyle = (element: HTMLStyleElement): boolean => {
  return !!element.innerHTML.trim();
};

/**
 * Validate if a base element should be injected
 */
const isValidBase = (element: HTMLBaseElement): boolean => {
  return !!element.href;
};

/**
 * Insert element at the beginning of document head
 */
const insertAtHeadStart = (element: Node): void => {
  document.head.insertBefore(element, document.head.firstChild);
};

/**
 * Copy attributes from source element to target element
 */
const copyAttributes = (
  source: Element,
  target: HTMLElement,
  nonce?: string,
): void => {
  if (nonce && (target instanceof HTMLScriptElement || 
                target instanceof HTMLLinkElement || 
                target instanceof HTMLStyleElement)) {
    target.setAttribute('nonce', nonce);
  }
  Array.from(source.attributes).forEach((attr) => {
    target.setAttribute(attr.name, attr.value);
  });
};

/**
 * Inject a script element into document head
 */
const injectScript = (element: HTMLScriptElement, nonce?: string): void => {
  if (!isValidScript(element)) return;

  const script = document.createElement('script');
  if (nonce) script.setAttribute('nonce', nonce);
  if (element.src) script.setAttribute('src', element.src);
  if (element.innerHTML) script.innerHTML = element.innerHTML;
  insertAtHeadStart(script);
};

/**
 * Inject a meta element into document head
 */
const injectMeta = (element: HTMLMetaElement): void => {
  if (!isValidMeta(element)) return;

  const meta = document.createElement('meta');
  copyAttributes(element, meta);
  insertAtHeadStart(meta);
};

/**
 * Inject a link element into document head
 */
const injectLink = (element: HTMLLinkElement, nonce?: string): void => {
  if (!isValidLink(element)) return;

  const link = document.createElement('link');
  copyAttributes(element, link, nonce);
  insertAtHeadStart(link);
};

/**
 * Inject a style element into document head
 */
const injectStyle = (element: HTMLStyleElement, nonce?: string): void => {
  if (!isValidStyle(element)) return;

  const style = document.createElement('style');
  if (nonce) style.setAttribute('nonce', nonce);
  style.innerHTML = element.innerHTML;
  insertAtHeadStart(style);
};

/**
 * Inject a base element into document head
 */
const injectBase = (element: HTMLBaseElement): void => {
  if (!isValidBase(element)) return;

  const base = document.createElement('base');
  copyAttributes(element, base);
  insertAtHeadStart(base);
};

/**
 * Inject a noscript element into document head
 */
const injectNoscript = (customHeadCode: string): void => {
  // eslint-disable-next-line
  const parser = new DOMParser();
  const content = parser.parseFromString(customHeadCode, 'text/xml');
  const noscriptElements = content.getElementsByTagName('noscript');
  
  if (noscriptElements.length > 0) {
    const noscript = document.createElement('noscript');
    noscript.innerHTML = noscriptElements[0].innerHTML;
    insertAtHeadStart(noscript);
  }
};

/**
 * Parse custom head code and inject HTML comments
 * @param customHeadCode - HTML string containing custom head code
 * @returns Parsed document or null if should skip
 */
const parseAndInjectComments = (customHeadCode: string): Document | null => {
  // eslint-disable-next-line
  const parser = new DOMParser();
  const customCode = parser.parseFromString(customHeadCode, 'text/html');

  // Add HTML comments
  customCode.childNodes.forEach((child) => {
    // eslint-disable-next-line
    if (child.nodeType === Node.COMMENT_NODE) {
      const commentNode = document.createComment(child.nodeValue);
      insertAtHeadStart(commentNode);
    }
  });

  // Handle malformed HTML
  if (customCode.body.innerText !== '') {
    const commentNode = document.createComment(customHeadCode);
    insertAtHeadStart(commentNode);
    return null;
  }

  return customCode;
};

/**
 * Process and inject a single element based on its type
 */
const processElement = (
  element: Element,
  customHeadCode: string,
  nonce?: string,
): void => {
  switch (element.tagName.toUpperCase()) {
    case 'SCRIPT':
      injectScript(element as HTMLScriptElement, nonce);
      break;
    case 'META':
      injectMeta(element as HTMLMetaElement);
      break;
    case 'LINK':
      injectLink(element as HTMLLinkElement, nonce);
      break;
    case 'STYLE':
      injectStyle(element as HTMLStyleElement, nonce);
      break;
    case 'BASE':
      injectBase(element as HTMLBaseElement);
      break;
    case 'NOSCRIPT':
      injectNoscript(customHeadCode);
      break;
    default:
      break;
  }
};

/**
 * Custom hook to inject custom head code into document head
 * @param customHeadCode - HTML string containing custom head code
 * @param isInsideAdminView - Whether component is in admin view
 * @param isInsideDevicePreview - Whether component is in device preview
 * @param nonce - CSP nonce for inline scripts and styles
 */
const useCustomHeadCode = (
  customHeadCode: string,
  isInsideAdminView: boolean,
  isInsideDevicePreview: boolean,
  nonce?: string,
): void => {
  useEffect(() => {
    if (isInsideAdminView || isInsideDevicePreview || isEmpty(customHeadCode)) {
      return;
    }

    const parsedDocument = parseAndInjectComments(customHeadCode);
    if (!parsedDocument) {
      return;
    }

    // Process elements in reverse order to maintain original sequence when using insertBefore
    const elements = Array.from(
      parsedDocument.head.getElementsByTagName('*'),
    ).reverse();
    
    elements.forEach((element) => {
      processElement(element, customHeadCode, nonce);
    });
  }, []);
};

export default useCustomHeadCode;
